import type { SolanaFeeQuote } from "../../joinsplit-v2/solana/fees";
import React from "react";
import { WalletFeeSummary } from "../../base-app/components/WalletFeeSummary";
import {
  formatSolanaTokenAmount,
  solanaTokenLabel,
  type SolanaTokenInfo,
} from "../../config/solana";
import type { SolanaDepositQuote } from "../hooks/useSolanaDepositQuote";
import type { solanaSendAmounts } from "../helpers/amounts";

export function SolanaDepositFeeSummary({
  token,
  amount,
  quote,
  error,
}: {
  token: SolanaTokenInfo;
  amount: bigint;
  quote: SolanaDepositQuote | null;
  error?: string | null;
}) {
  if (!quote)
    return (
      <p role="status" className="wallet-review-context">
        {error || "Estimating deposit network fee…"}
      </p>
    );
  const sol = (value: bigint) => `${formatSolanaTokenAmount(value, 9)} SOL`;
  return (
    <WalletFeeSummary
      rows={[
        { label: "Protocol fee", value: `0 ${solanaTokenLabel(token)}` },
        {
          label: "Estimated network fee (conservative)",
          value: sol(quote.networkFee),
        },
        { label: "Account rent", value: sol(quote.accountRent) },
        {
          label: "Total estimated cost",
          value: sol(quote.networkFee + quote.accountRent),
        },
      ]}
      received={`${formatSolanaTokenAmount(amount, token.decimals)} ${solanaTokenLabel(token)}`}
    >
      <div>Network fees and rent are paid separately in SOL.</div>
      <div>
        Max also leaves {sol(quote.buffer)} as a safety reserve. Unused SOL
        stays in your wallet.
      </div>
    </WalletFeeSummary>
  );
}

export function SolanaSendFeeSummary({
  token,
  preview,
  batch = false,
  privateTransfer = false,
  quote,
  error,
}: {
  token: SolanaTokenInfo;
  preview: ReturnType<typeof solanaSendAmounts>;
  batch?: boolean;
  privateTransfer?: boolean;
  quote?: SolanaFeeQuote | null;
  error?: string | null;
}) {
  if (!privateTransfer && !quote)
    return (
      <p role="status" className="wallet-review-context">
        {error || "Estimating withdrawal fee…"}
      </p>
    );
  const format = (value: bigint) =>
    `${formatSolanaTokenAmount(value, token.decimals)} ${solanaTokenLabel(token)}`;
  return (
    <WalletFeeSummary
      rows={[
        ...(quote
          ? [
              { label: "Network fee", value: format(BigInt(quote.networkFee)) },
              {
                label: "Account creation",
                value: format(BigInt(quote.accountRent)),
              },
              ...(BigInt(quote.relayerMargin) > 0n
                ? [
                    {
                      label: "Relayer margin",
                      value: format(BigInt(quote.relayerMargin)),
                    },
                  ]
                : []),
            ]
          : []),
        { label: "Total fee", value: format(preview.fee) },
        ...(batch
          ? [
              {
                label: "Total from private balance",
                value: format(preview.total),
              },
            ]
          : []),
      ]}
      received={format(preview.received)}
    >
      <div>
        {privateTransfer
          ? "Network fees are sponsored for private transfers on Solana Devnet."
          : batch
            ? "The relayer fee is added to the recipient amounts."
            : "The fee is deducted from the amount. Refundable temporary-account rent is excluded."}
      </div>
    </WalletFeeSummary>
  );
}
